import { GetOptions, Inventory } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { getNextPageParam } from "../utils/page-params";

const databaseService = serviceHost.getDatabaseService();
const inventoryRepository = repositoryHost.getInventoryRepository(databaseService);
type CreateInventoryParams = Parameters<typeof inventoryRepository.create>[0];
type UpdateInventoryParams = Parameters<typeof inventoryRepository.update>[0];

export const useInventories = (options: GetOptions<Inventory>) => {
  return useInfiniteQuery({
    queryKey: ["inventories", "get", JSON.stringify(options)],
    queryFn: ({ pageParam }) =>
      inventoryRepository.getAll({
        ...options,
        pagination: {
          ...options.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(options),
  });
};

/**
 * Loads every inventory by eagerly paginating through all pages. Firestore has no
 * case-insensitive substring search, so the inventory list filters locally — which
 * requires the full inventory set to be present so search/sort cover every record,
 * not just the most-recently-updated page. `hasNextPage` stays true until the last
 * page is in, so callers can treat it as "still loading".
 */
export const useAllInventories = () => {
  const query = useInventories({
    pagination: { limit: 200 },
    orderBy: { field: "updatedAt", direction: "desc" },
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return query;
};

export const useInventoryById = (inventoryId: string) => {
  return useQuery({
    queryKey: ["inventories", "get-by-id", inventoryId],
    enabled: inventoryId.length > 0,
    queryFn: () => inventoryRepository.get({ id: inventoryId }),
  });
};

export const useCreateInventory = () => {
  const queryClient = useQueryClient();

  return useMutation<string, Error, CreateInventoryParams>({
    mutationKey: ["inventories", "create"],
    mutationFn: async (params: CreateInventoryParams) =>
      inventoryRepository.create(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useUpdateInventory = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateInventoryParams>({
    mutationKey: ["inventories", "update"],
    mutationFn: async (params: UpdateInventoryParams) =>
      inventoryRepository.update(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({
        queryKey: ["inventories", "get-by-id", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};
